<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageList from './MessageList.vue';
import PlaygroundComposer from './PlaygroundComposer.vue';
import PlaygroundDebugPanel from './PlaygroundDebugPanel.vue';
import CaptainAssistant from 'dashboard/api/captain/assistant';

const { assistantId } = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const messages = ref([]);
const isLoading = ref(false);
const quotedProductId = ref(null);
const lastDebug = ref(null);
const composerKey = ref(0);

const extractDebug = data => {
  if (!data || typeof data !== 'object') return null;
  return data.debug && typeof data.debug === 'object' ? data.debug : null;
};

const formatMessagesForApi = () => {
  let remainingImages = 2;
  return [...messages.value]
    .reverse()
    .map(message => {
      const payload = {
        role: message.sender,
        content: message.content,
      };

      if (message.sender === 'assistant' && message.agentName) {
        payload.agent_name = message.agentName;
      }

      const image = message.imageDataUrl;
      if (
        remainingImages > 0 &&
        image &&
        (image.startsWith('data:image/') || image.startsWith('http'))
      ) {
        payload.image_url = image;
        remainingImages -= 1;
      }

      return payload;
    })
    .reverse();
};

const resetConversation = () => {
  messages.value = [];
  quotedProductId.value = null;
  lastDebug.value = null;
  composerKey.value += 1;
};

const splitReplyParts = content => {
  if (typeof content !== 'string' || !content.includes('||')) return [content];
  const parts = content
    .split('||')
    .map(part => part.trim())
    .filter(Boolean)
    .slice(0, 4);
  return parts.length ? parts : [content];
};

const displayMessages = computed(() =>
  messages.value.flatMap(message => {
    if (message.sender !== 'assistant') return [message];
    return splitReplyParts(message.content).map((content, index) => ({
      ...message,
      content,
      productImageUrl: index === 0 ? message.productImageUrl : undefined,
    }));
  })
);

watch(
  () => assistantId,
  (newId, oldId) => {
    if (oldId && newId !== oldId) {
      resetConversation();
    }
  }
);

const sendMessage = async payload => {
  if (isLoading.value) return;

  const history = formatMessagesForApi();
  const userMessage = {
    content:
      payload.text ||
      (payload.audioDataUrl ? t('CAPTAIN.PLAYGROUND.VOICE_NOTE') : ''),
    sender: 'user',
    timestamp: new Date().toISOString(),
    imageUrl: payload.imagePreviewUrl || payload.imageDataUrl || undefined,
    imageDataUrl: payload.imageDataUrl || undefined,
    audioUrl: payload.audioPreviewUrl || undefined,
  };
  messages.value.push(userMessage);

  try {
    isLoading.value = true;
    const { data } = await CaptainAssistant.playground({
      assistantId,
      messageContent: payload.text,
      messageHistory: history,
      quotedProductId: quotedProductId.value,
      imageDataUrl: payload.imageDataUrl,
      audioDataUrl: payload.audioDataUrl,
    });

    if (data.quoted_product_id) {
      quotedProductId.value = data.quoted_product_id;
    }

    if (data.transcription) {
      userMessage.content = [payload.text, data.transcription]
        .filter(Boolean)
        .join('\n');
    }

    lastDebug.value = extractDebug(data);

    messages.value.push({
      content:
        data.response || data.content || t('CAPTAIN.PLAYGROUND.ERROR_MESSAGE'),
      sender: 'assistant',
      agentName: data.agent_name,
      productImageUrl: data.product_image_url || undefined,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting assistant response:', error);
    const errorData = error?.response?.data || {};
    const apiMessage = errorData.response || errorData.content;
    messages.value.push({
      content: apiMessage || t('CAPTAIN.PLAYGROUND.ERROR_MESSAGE'),
      sender: 'assistant',
      timestamp: new Date().toISOString(),
    });
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col h-full rounded-xl border py-6 border-n-weak text-n-slate-11"
  >
    <div class="mb-8 px-6">
      <div class="flex justify-between items-center mb-1">
        <h3 class="text-lg font-medium">
          {{ t('CAPTAIN.PLAYGROUND.HEADER') }}
        </h3>
        <NextButton
          ghost
          sm
          slate
          icon="i-lucide-rotate-ccw"
          @click="resetConversation"
        />
      </div>
      <p class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.PLAYGROUND.DESCRIPTION') }}
      </p>
    </div>

    <MessageList :messages="displayMessages" :is-loading="isLoading" />

    <PlaygroundDebugPanel :debug="lastDebug" />

    <PlaygroundComposer
      :key="composerKey"
      :disabled="isLoading"
      @send="sendMessage"
    />

    <p class="text-xs text-n-slate-11 pt-2 text-center">
      {{ t('CAPTAIN.PLAYGROUND.CREDIT_NOTE') }}
    </p>
  </div>
</template>
